import { useMemo, useState } from "react";
import { useQuery } from "react-query";
import {
  Box,
  Button,
  Center,
  Flex,
  Image,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Spinner,
  Text,
  VStack,
} from "@chakra-ui/react";
import { DateRangePicker } from "../statistics/DateRangePicker";
import { getBooksForPublisher } from "../../services/api/books";
import type { Book } from "../../types/database";

const PAGE = 20;
const monthStart = () => {
  const d = new Date();
  return new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), 1)).toISOString().slice(0, 10);
};
const bookCover = (b: Book) =>
  b.cover || String((b.audioBookMap as Record<string, unknown> | null)?.cover ?? "");

export function ActivityDialog({
  publisherName,
  onClose,
}: {
  publisherName: string | null;
  onClose: () => void;
}) {
  const [start, setStart] = useState(monthStart());
  const [end, setEnd] = useState(new Date().toISOString().slice(0, 10));
  const [visible, setVisible] = useState(PAGE);

  const isOpen = !!publisherName;
  const booksQuery = useQuery(
    ["activity-books", publisherName],
    () => getBooksForPublisher(publisherName as string),
    { enabled: isOpen },
  );

  // Books created within the selected range (mirrors the Flutter createdAt query).
  const filtered = useMemo(() => {
    const lo = new Date(`${start}T00:00:00.000Z`).getTime();
    const hi = new Date(`${end}T23:59:59.999Z`).getTime();
    return (booksQuery.data ?? [])
      .filter((b) => {
        if (!b.createdAt) return false;
        const ts = new Date(b.createdAt).getTime();
        return !Number.isNaN(ts) && ts >= lo && ts <= hi;
      })
      .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
  }, [booksQuery.data, start, end]);

  const shown = filtered.slice(0, visible);

  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered size="xl" scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent maxH="80vh">
        <ModalHeader>{publisherName}</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <Flex gap={3} mb={4} wrap="wrap" align="center">
            <DateRangePicker
              start={start}
              end={end}
              placeholder={"Обрати діапазон"}
              onChange={(s, e) => {
                setStart(s);
                setEnd(e);
                setVisible(PAGE);
              }}
            />
          </Flex>

          {booksQuery.isLoading ? (
            <Center py={10}>
              <Spinner color="brand.orange" />
            </Center>
          ) : filtered.length === 0 ? (
            <Center py={10}>
              <Text color="gray.500">{"Немає результатів"}</Text>
            </Center>
          ) : (
            <VStack align="stretch" spacing={2} pb={2}>
              {shown.map((b) => (
                <Flex
                  key={b.id}
                  gap={3}
                  align="center"
                  p={2}
                  border="1px solid"
                  borderColor="blackAlpha.100"
                  borderRadius="md"
                >
                  <Image
                    src={bookCover(b) || "/book-placeholder.png"}
                    fallbackSrc="/book-placeholder.png"
                    alt={b.name}
                    w="42px"
                    h="60px"
                    objectFit="cover"
                    borderRadius="sm"
                    flexShrink={0}
                  />
                  <Box minW={0}>
                    <Text fontSize="xs" color="gray.400" noOfLines={1}>
                      {b.id}
                    </Text>
                    <Text fontWeight="medium" noOfLines={1}>
                      {b.name || "(без назви)"}
                    </Text>
                    <Text fontSize="sm" color="gray.500" noOfLines={1}>
                      {(b.authors ?? []).join(", ")}
                    </Text>
                  </Box>
                </Flex>
              ))}
              {visible < filtered.length && (
                <Button
                  variant="outline"
                  size="sm"
                  alignSelf="center"
                  mt={2}
                  onClick={() => setVisible((v) => v + PAGE)}
                >
                  {"Показати ще"}
                </Button>
              )}
            </VStack>
          )}
        </ModalBody>
      </ModalContent>
    </Modal>
  );
}
